import {
  loadTossPayments,
  TossPaymentsWidgets,
} from '@tosspayments/tosspayments-sdk'
import { isNil } from 'lodash-es'
import { useParams, useRouter } from 'next/navigation'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-toastify'

import { TPaymentResult, TPaymentWidgetInfo } from '@/entities/payment'
import { MessageType } from '@/shared/api'
import { useErrorDialogStore } from '@/shared/store'

import { useRequestTossPaymentConfirm } from '../api/request-toss-payment-confirm'
import { isPaymentError } from '../model/error'

const CURRENCY = 'KRW' // 일반 결제는 KRW만 지원
const PAYMENT_VARIANT_KEY = 'CONTRACT-PC'
const AGREEMENT_VARIANT_KEY = 'AGREEMENT'
export const PAYMENT_SELECTOR = 'payment-method'
export const AGREEMENT_SELECTOR = 'agreement'

const AGREEMENT_ERROR = 'NEED_AGREEMENT_WITH_REQUIRED_TERMS'
const CARD_SELECTION_ERROR = 'NEED_CARD_PAYMENT_DETAIL'

type Params = {
  contractId: string
}

type TossWidgetState = {
  isWidgetReady: boolean
  widgets: TossPaymentsWidgets | null
  fetchPaymentWidgets: (widgetInfo: TPaymentWidgetInfo) => Promise<void>
  renderPaymentWidgets: () => Promise<void>
  requestPayment: () => Promise<TPaymentResult | undefined>
  confirmPayment: (paymentResult: TPaymentResult) => void
  isConfirming: boolean
}

const TossWidgetContext = createContext<TossWidgetState | null>(null)

type Props = {
  children: ReactNode
}

export const TossWidgetProvider = ({ children }: Props) => {
  const params = useParams<Params>()
  const contractId = params?.contractId

  const router = useRouter()

  const { actions } = useErrorDialogStore()

  const [ready, setReady] = useState(false)

  const [widgets, setWidgets] = useState<TossPaymentsWidgets | null>(null)

  const widgetInfoRef = useRef<TPaymentWidgetInfo | null>(null)

  const requestTossPaymentConfirm = useRequestTossPaymentConfirm({
    onSuccess: () => {
      router.push(`/guest/contract/${contractId}/payment?result=success`)
    },
    onError: () => {
      window.location.reload()
    },
  })

  const fetchPaymentWidgets = useCallback(
    async (widgetInfo: TPaymentWidgetInfo) => {
      if (isNil(widgetInfo)) {
        return
      }

      const { clientKey, customerKey } = widgetInfo

      // 결제위젯 초기화
      const tossPayments = await loadTossPayments(clientKey)

      // 회원 결제
      const widgets = tossPayments.widgets({
        customerKey,
      })

      setWidgets(widgets)
      widgetInfoRef.current = widgetInfo
    },
    [],
  )

  const renderPaymentWidgets = useCallback(async () => {
    if (widgets === null || isNil(widgetInfoRef.current)) {
      return
    }

    const { amount } = widgetInfoRef.current

    // 주문 결제 금액 설정
    await widgets.setAmount({
      value: amount,
      currency: CURRENCY,
    })

    await Promise.all([
      widgets.renderPaymentMethods({
        selector: `#${PAYMENT_SELECTOR}`,
        variantKey: PAYMENT_VARIANT_KEY,
      }),
      widgets.renderAgreement({
        selector: `#${AGREEMENT_SELECTOR}`,
        variantKey: AGREEMENT_VARIANT_KEY,
      }),
    ])

    setReady(true)
  }, [widgets])

  const handlePaymentError = useCallback(
    (error: unknown) => {
      if (!isPaymentError(error)) {
        toast('결제 중 오류가 발생했습니다.')

        return
      }

      if (error.code === AGREEMENT_ERROR) {
        actions.open({
          content: '필수 약관에 동의해 주세요.',
          type: MessageType.ALERT,
        })
      } else if (error.code === CARD_SELECTION_ERROR) {
        actions.open({
          content: '카드사를 선택해 주세요.',
          type: MessageType.ALERT,
        })
      } else {
        toast(error.message)
      }
    },
    [actions],
  )

  const requestPayment = useCallback(async () => {
    if (widgets === null || isNil(widgetInfoRef.current)) {
      return
    }

    const { orderId, orderName, customerEmail, customerName } =
      widgetInfoRef.current

    try {
      const { paymentType, ...result } = await widgets.requestPayment({
        orderId,
        orderName,
        customerEmail,
        customerName,
      })

      return {
        ...result,
        amount: result.amount.value,
      }
    } catch (error) {
      handlePaymentError(error)
    }
  }, [handlePaymentError, widgets])

  const confirmPayment = useCallback(
    (paymentResult: TPaymentResult) => {
      requestTossPaymentConfirm.execute(paymentResult)
    },
    [requestTossPaymentConfirm],
  )

  const value = useMemo(
    () => ({
      isWidgetReady: ready,
      widgets,
      fetchPaymentWidgets,
      renderPaymentWidgets,
      requestPayment,
      confirmPayment,
      isConfirming: requestTossPaymentConfirm.isExecuting,
    }),
    [
      ready,
      widgets,
      fetchPaymentWidgets,
      renderPaymentWidgets,
      requestPayment,
      confirmPayment,
      requestTossPaymentConfirm.isExecuting,
    ],
  )

  return (
    <TossWidgetContext.Provider value={value}>
      {children}
    </TossWidgetContext.Provider>
  )
}

export const useTossWidget = () => {
  const context = useContext(TossWidgetContext)

  if (isNil(context)) {
    throw new Error('useTossWidget must be used within a TossWidgetProvider')
  }

  return context
}
